import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { css } from '@linaria/core';
import { MapContainer, TileLayer, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { parseGeoPoint, parseGeoShape } from '../utils/geoPoint';
import { decodeGeotile } from '../utils/geotile';
import { computeLatLngsExtent, computeMarkersExtent } from '../utils/mapExtent';
import { LOCATION_FILTER_FIELD } from '../utils/uiFilters';
import { useMapState } from '../contexts/MapStateContext';
import { useDebounce } from '../hooks/useDebounce';
import {
  MAP_MAX_NATIVE_ZOOM,
  MAP_MAX_ZOOM,
  OS_TILE_URL,
  aerialTileUrl,
} from '../utils/mapTiles';
import {
  HEX_ANCHOR,
  HEX_SIZE,
  HEX_ZOOM_THRESHOLD,
  LOCATION_OUTLINE_STYLE,
  PIN_ANCHOR,
  PIN_SIZE,
  POLYGON_CASING_HIDDEN_STYLE,
  POLYGON_DEFAULT_STYLE,
  POLYGON_FOCUS_CASING_STYLE,
  POLYGON_FOCUS_STYLE,
  hexAnchorFor,
} from '../utils/mapMarkerStyles';
import {
  encodeMapBounds,
  getMapBoundsFromFilters,
  getMapBoundsFromURL,
  encodeShapeFilters,
  getShapeFiltersFromFilters,
} from '../utils/mapFilters';
import { renderMapImage } from '../utils/mapPrint';
import DrawControl from './DrawControl';
import MapPrintSheet from './MapPrintSheet';
import {
  BASEMAPS,
  DrawCursorTooltip,
  LeftControlStack,
  MapCopyright,
  MapScale,
  ResultPopup,
  RightControlStack,
  ScaleComputer,
  SidebarCollapseTab,
  ToolPanel,
  createHexIcon,
  createPinIcon,
} from './MapControls';

const mapContainer = css`
  position: relative;
  height: 810px;
  width: 100%;
  margin-top: 20px;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 520px;
  }

  :global() {
    .leaflet-container {
      height: 100%;
      width: 100%;
      /* Isolate Leaflet's z-index stack so the corner scale/copyright and the
         result popup layer above the map. */
      isolation: isolate;
    }

    .leaflet-control-attribution {
      background-color: rgba(255, 255, 255, 0.8) !important;
      font-family: 'articulat-cf', Tahoma, Verdana;
      font-size: 12px;
      font-weight: 400;
      line-height: 26px;
      letter-spacing: 0.24px;
      color: #000000;
      padding: 2px 8px;
      margin: 10px !important;
    }

    .leaflet-control-attribution a {
      color: #000000;
      font-weight: 400;
      text-decoration: underline;
    }

    .he-map-pin {
      background: transparent !important;
      border: 0 !important;
    }

    .he-map-pin svg {
      display: block;
    }

    .he-map-hex {
      background: transparent !important;
      border: 0 !important;
    }

    .he-map-hex svg {
      display: block;
    }

    .map-control-element {
      pointer-events: auto;
    }

    .leaflet-draw-tooltip {
      display: none !important;
    }

    .leaflet-editing-icon {
      background-color: #ffffff;
      border: 2px solid #4786ed;
      border-radius: 50%;
    }

    .cluster-badge {
      background-color: #f8bb25;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 12px;
      color: #1a1a1a;
      border: 2px solid #fff;
      box-sizing: border-box;
      cursor: pointer;
    }

    .cluster-badge.cluster-medium {
      background-color: #facc5c;
    }

    .cluster-badge.cluster-large {
      background-color: #f57d73;
    }
  }
`;

const mapNotice = css`
  color: #1a1a1a;
  font-size: 18px;
  margin-top: 10px;

  a {
    color: #1a1a1a;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;

    &:hover {
      color: #1a1a1a;
      text-decoration-thickness: 2px;
      text-underline-offset: 2px;
    }
  }
`;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

function createCountBadge(count) {
  let sizeClass = '';
  let dimensions = 30;

  if (count >= 100 && count < 1000) {
    sizeClass = 'cluster-medium';
    dimensions = 40;
  } else if (count >= 1000) {
    sizeClass = 'cluster-large';
    dimensions = 50;
  }

  return L.divIcon({
    html: `<span>${count.toLocaleString()}</span>`,
    className: `cluster-badge ${sizeClass}`,
    iconSize: [dimensions, dimensions],
    iconAnchor: [dimensions / 2, dimensions / 2],
  });
}

// Result thumbnails are relative paths; prepend the HE origin on local hosts so
// the image resolves in development.
function buildThumbUrl(imageUrl) {
  if (!imageUrl) return undefined;
  if (/^https?:\/\//.test(imageUrl)) return imageUrl;
  const host = window.location.hostname;
  const base =
    host === 'localhost' || host === 'local-web.historicengland.org.uk.test'
      ? 'https://historicengland.org.uk'
      : '';
  return `${base}${imageUrl}?autorotate=true&width=440&height=312&mode=crop&quality=90&upscale=true`;
}

function rawField(result, field) {
  const value = result?.[field];
  return value && typeof value === 'object' && 'raw' in value
    ? value.raw
    : value;
}

function buildGatewayPopupResult(selected) {
  const result = selected.result || {};

  const entries = [
    { label: 'Location', value: rawField(result, 'location') },
    { label: 'Ref No', value: rawField(result, 'refNo') },
    { label: 'Monument type', value: rawField(result, 'monumentType') },
  ].filter(entry => entry.value != null && entry.value !== '');

  return {
    title: selected.title,
    url: rawField(result, 'path'),
    category: rawField(result, 'dataset'),
    entries,
  };
}

function buildPopupResult(selected, searchType) {
  if (searchType === 'heritage-gateway') {
    return buildGatewayPopupResult(selected);
  }

  const result = selected.result || {};
  const grade = rawField(result, 'grade');
  const category = rawField(result, 'category');
  const designation = rawField(result, 'designation');
  const categoryLine =
    [grade ? `Grade ${grade}` : null, category].filter(Boolean).join(' ') ||
    designation ||
    null;

  const id = rawField(result, 'id');
  const listEntries = rawField(result, 'listEntries');
  const listEntry =
    id ||
    (Array.isArray(listEntries) ? listEntries.join(', ') : listEntries) ||
    null;

  const comments = rawField(result, 'comments');
  const showComments =
    comments != null &&
    category !== 'Certificate of Immunity' &&
    category !== 'Building Preservation Notice';

  return {
    title: selected.title,
    url: rawField(result, 'path'),
    image: buildThumbUrl(rawField(result, 'imageUrl')),
    category: categoryLine,
    listEntry,
    comments: showComments ? comments : null,
  };
}

const TOOL_BUTTON_INDEX = { line: 0, polygon: 1, rectangle: 2, circle: 3 };

const COORD_PRECISION = 1e5; // 5 decimal places, ~1.1m

function roundCoord(val) {
  return Math.round(val * COORD_PRECISION) / COORD_PRECISION;
}

function viewportsMatch(a, b) {
  if (!a || !b) return false;
  return (
    a.zoom === b.zoom &&
    roundCoord(a.bounds.topLeft.lat) === roundCoord(b.bounds.topLeft.lat) &&
    roundCoord(a.bounds.topLeft.lng) === roundCoord(b.bounds.topLeft.lng) &&
    roundCoord(a.bounds.bottomRight.lat) ===
      roundCoord(b.bounds.bottomRight.lat) &&
    roundCoord(a.bounds.bottomRight.lng) ===
      roundCoord(b.bounds.bottomRight.lng)
  );
}

function normalizeViewport(viewport) {
  return {
    zoom: viewport.zoom,
    bounds: {
      topLeft: {
        lat: roundCoord(viewport.bounds.topLeft.lat),
        lng: roundCoord(viewport.bounds.topLeft.lng),
      },
      bottomRight: {
        lat: roundCoord(viewport.bounds.bottomRight.lat),
        lng: roundCoord(viewport.bounds.bottomRight.lng),
      },
    },
  };
}

function MapEventHandler({
  onViewportChange,
  lastTriggeredViewportRef,
  isLayerUpdateRef,
}) {
  const isFirstMoveEnd = useRef(true);
  const hasInitializedViewport = useRef(false);
  const onViewportChangeRef = useRef(onViewportChange);
  onViewportChangeRef.current = onViewportChange;

  const map = useMapEvents({
    moveend: () => {
      if (isLayerUpdateRef && isLayerUpdateRef.current) {
        return;
      }

      if (isFirstMoveEnd.current) {
        isFirstMoveEnd.current = false;
        return;
      }

      const bounds = map.getBounds();
      const newViewport = {
        zoom: map.getZoom(),
        bounds: {
          topLeft: { lat: bounds.getNorth(), lng: bounds.getWest() },
          bottomRight: { lat: bounds.getSouth(), lng: bounds.getEast() },
        },
      };

      if (viewportsMatch(lastTriggeredViewportRef.current, newViewport)) {
        return;
      }

      onViewportChangeRef.current(newViewport);
    },
  });

  useEffect(() => {
    if (!hasInitializedViewport.current) {
      hasInitializedViewport.current = true;
      const bounds = map.getBounds();
      onViewportChangeRef.current({
        zoom: map.getZoom(),
        bounds: {
          topLeft: { lat: bounds.getNorth(), lng: bounds.getWest() },
          bottomRight: { lat: bounds.getSouth(), lng: bounds.getEast() },
        },
      });
    }
  }, [map]);

  return null;
}

function GeotileMarkers({ tiles, showGrid, isLayerUpdateRef }) {
  const map = useMap();

  useEffect(() => {
    if (isLayerUpdateRef) {
      isLayerUpdateRef.current = true;
    }

    const layerGroup = L.layerGroup();

    tiles.forEach(tile => {
      if (showGrid) {
        const rect = L.rectangle(tile.bounds, {
          color: '#F8BB25',
          weight: 1,
          fill: false,
        });

        rect.on('click', () => {
          map.fitBounds(tile.bounds, { animate: true });
        });

        layerGroup.addLayer(rect);
      }

      const badge = L.marker(tile.center, {
        icon: createCountBadge(tile.count),
        keyboard: false,
      });

      badge.on('click', () => {
        map.fitBounds(tile.bounds, { animate: true });
      });

      layerGroup.addLayer(badge);
    });

    map.addLayer(layerGroup);

    if (isLayerUpdateRef) {
      isLayerUpdateRef.current = false;
    }

    return () => {
      if (isLayerUpdateRef) {
        isLayerUpdateRef.current = true;
      }
      map.removeLayer(layerGroup);
      Promise.resolve().then(() => {
        if (isLayerUpdateRef) {
          isLayerUpdateRef.current = false;
        }
      });
    };
  }, [map, tiles, showGrid, isLayerUpdateRef]);

  return null;
}

const FOCUS_CLIP_RECT = 'M-1000000 -1000000 H1000000 V1000000 H-1000000 Z';
let focusClipSeq = 0;

const SVG_NS = 'http://www.w3.org/2000/svg';

function setupFocusClip(casing) {
  const path = casing?._path;
  if (!path) return null;

  const svg = path.ownerSVGElement;
  if (!svg) return null;

  let defs = svg.querySelector('defs');
  if (!defs) {
    defs = document.createElementNS(SVG_NS, 'defs');
    svg.appendChild(defs);
  }

  const id = `he-focus-clip-${(focusClipSeq += 1)}`;
  const clip = document.createElementNS(SVG_NS, 'clipPath');
  clip.setAttribute('id', id);
  const clipPath = document.createElementNS(SVG_NS, 'path');
  clipPath.setAttribute('clip-rule', 'evenodd');
  clip.appendChild(clipPath);
  defs.appendChild(clip);

  path.setAttribute('clip-path', `url(#${id})`);
  casing.__focusClip = clip;
  casing.__focusClipPath = clipPath;
  return clip;
}

function syncFocusClip(casing) {
  const d = casing?._path?.getAttribute('d');
  if (casing?.__focusClipPath && d) {
    casing.__focusClipPath.setAttribute('d', `${FOCUS_CLIP_RECT} ${d}`);
  }
}

// Is a container point inside an icon anchored at `anchorPoint`? Mirrors the
// divIcon's own iconSize/iconAnchor so the hit area matches what is drawn.
function withinIcon(point, anchorPoint, size, anchor) {
  const left = anchorPoint.x - anchor[0];
  const top = anchorPoint.y - anchor[1];
  return (
    point.x >= left &&
    point.x <= left + size[0] &&
    point.y >= top &&
    point.y <= top + size[1]
  );
}

function withinRing(latLng, ring) {
  const [lat, lng] = latLng;
  let inside = false;

  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [latI, lngI] = ring[i];
    const [latJ, lngJ] = ring[j];

    if (
      latI > lat !== latJ > lat &&
      lng < ((lngJ - lngI) * (lat - latI)) / (latJ - latI) + lngI
    ) {
      inside = !inside;
    }
  }

  return inside;
}

// Depth-tolerant point-in-shape for what parseGeoShape produces: a Polygon is
// [outerRing, ...holes] and a MultiPolygon nests one level deeper. A point in a
// hole is outside the shape.
function withinShape(latLng, shape) {
  if (!Array.isArray(shape) || shape.length === 0) return false;

  const isRingList = Array.isArray(shape[0]?.[0]);

  if (!isRingList) {
    return withinRing(latLng, shape);
  }

  const isMultiPolygon = Array.isArray(shape[0][0][0]);

  if (isMultiPolygon) {
    return shape.some(polygon => withinShape(latLng, polygon));
  }

  const [outer, ...holes] = shape;
  return (
    withinRing(latLng, outer) && !holes.some(hole => withinRing(latLng, hole))
  );
}

function IndividualMarkers({ markers, onSelect, selectedId }) {
  const map = useMap();
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const markerRefsRef = useRef(new Map());
  const polygonRefsRef = useRef(new Map());
  const hexRefsRef = useRef(new Map());

  useEffect(() => {
    if (markers.length === 0) return undefined;

    const layerGroup = L.layerGroup();
    const hexLayer = L.layerGroup();
    const markerRefs = new Map();
    const polygonRefs = new Map();
    const hexRefs = new Map();

    // Leaflet only delivers a click to the topmost layer under the cursor, so
    // features stacked behind it are unreachable. Every feature routes its
    // click through here instead: the click point is tested against all of
    // them, and repeated clicks on the same overlapping set step through it.
    const cycle = { key: null, index: 0 };

    const featuresAt = event => {
      const point = map.mouseEventToContainerPoint(event.originalEvent);
      const clickLatLng = [event.latlng.lat, event.latlng.lng];
      const hexesVisible = map.getZoom() < HEX_ZOOM_THRESHOLD;

      // Split by the pane a feature is actually drawn in: pins, and the
      // hexagons that stand in for shapes when zoomed out, live in the marker
      // pane above the shapes, so they come first in the cycle. Within a pane,
      // later markers paint over earlier ones, hence the reverse. Order is
      // taken from the markers array rather than live z-index, which selection
      // mutates via setZIndexOffset.
      const inMarkerPane = [];
      const inOverlayPane = [];

      markers.forEach(item => {
        if (item.shape && !hexesVisible) {
          if (withinShape(clickLatLng, item.shape)) inOverlayPane.push(item);
          return;
        }

        const anchor = item.shape ? hexAnchorFor(item) : item.position;
        if (anchor == null) return;

        const size = item.shape ? HEX_SIZE : PIN_SIZE;
        const offset = item.shape ? HEX_ANCHOR : PIN_ANCHOR;
        if (
          withinIcon(point, map.latLngToContainerPoint(anchor), size, offset)
        ) {
          inMarkerPane.push(item);
        }
      });

      return [...inMarkerPane.reverse(), ...inOverlayPane.reverse()];
    };

    const handleFeatureClick = item => event => {
      const candidates = featuresAt(event);

      if (candidates.length === 0) {
        onSelectRef.current?.(item);
        return;
      }

      const key = candidates.map(candidate => candidate.id).join('|');
      cycle.index =
        cycle.key === key ? (cycle.index + 1) % candidates.length : 0;
      cycle.key = key;

      onSelectRef.current?.(candidates[cycle.index]);
    };

    markers.forEach(item => {
      if (item.shape) {
        const casing = L.polygon(item.shape, POLYGON_CASING_HIDDEN_STYLE);
        const polygon = L.polygon(item.shape, POLYGON_DEFAULT_STYLE);

        casing.on('click', handleFeatureClick(item));
        polygon.on('click', handleFeatureClick(item));

        layerGroup.addLayer(casing);
        layerGroup.addLayer(polygon);
        polygonRefs.set(item.id, { polygon, casing });

        const hexPoint = hexAnchorFor(item) || polygon.getBounds().getCenter();
        const hex = L.marker(hexPoint, {
          icon: createHexIcon(false),
          keyboard: false,
        });
        hex.on('click', handleFeatureClick(item));
        hexLayer.addLayer(hex);
        hexRefs.set(item.id, hex);
      } else {
        const marker = L.marker(item.position, {
          icon: createPinIcon(false),
          keyboard: false,
        });
        marker.on('click', handleFeatureClick(item));
        layerGroup.addLayer(marker);
        markerRefs.set(item.id, marker);
      }
    });

    map.addLayer(layerGroup);

    polygonRefs.forEach(({ casing }) => setupFocusClip(casing));
    const syncFocusClips = () =>
      polygonRefs.forEach(({ casing }) => syncFocusClip(casing));
    syncFocusClips();
    map.on('zoomend viewreset moveend', syncFocusClips);

    const updateHexVisibility = () => {
      const show = map.getZoom() < HEX_ZOOM_THRESHOLD;
      if (show && !map.hasLayer(hexLayer)) {
        map.addLayer(hexLayer);
      } else if (!show && map.hasLayer(hexLayer)) {
        map.removeLayer(hexLayer);
      }
    };
    updateHexVisibility();
    map.on('zoomend', updateHexVisibility);

    markerRefsRef.current = markerRefs;
    polygonRefsRef.current = polygonRefs;
    hexRefsRef.current = hexRefs;

    return () => {
      map.off('zoomend viewreset moveend', syncFocusClips);
      map.off('zoomend', updateHexVisibility);
      polygonRefs.forEach(({ casing }) => {
        casing.__focusClip?.remove();
        casing.__focusClip = null;
        casing.__focusClipPath = null;
      });
      map.removeLayer(layerGroup);
      if (map.hasLayer(hexLayer)) {
        map.removeLayer(hexLayer);
      }
      markerRefsRef.current = new Map();
      polygonRefsRef.current = new Map();
      hexRefsRef.current = new Map();
    };
  }, [map, markers]);

  useEffect(() => {
    markerRefsRef.current.forEach((layer, id) => {
      const isSelected = id === selectedId;
      layer.setIcon(createPinIcon(isSelected));
      layer.setZIndexOffset(isSelected ? 1000 : 0);
    });

    polygonRefsRef.current.forEach(({ polygon, casing }, id) => {
      const isSelected = id === selectedId;
      polygon.setStyle(
        isSelected ? POLYGON_FOCUS_STYLE : POLYGON_DEFAULT_STYLE,
      );
      casing.setStyle(
        isSelected ? POLYGON_FOCUS_CASING_STYLE : POLYGON_CASING_HIDDEN_STYLE,
      );
      if (isSelected) {
        casing.bringToFront();
        polygon.bringToFront();
      }
    });

    hexRefsRef.current.forEach((hex, id) => {
      const isSelected = id === selectedId;
      hex.setIcon(createHexIcon(isSelected));
      hex.setZIndexOffset(isSelected ? 1000 : 0);
    });
  }, [selectedId, markers]);

  return null;
}

function MapBoundsController({ targetBounds, onApplied }) {
  const map = useMap();
  const lastAppliedRef = useRef(null);

  useEffect(() => {
    if (!targetBounds) return;

    const { zoom, topLeft, bottomRight } = encodeMapBounds(targetBounds);
    const targetEncoded = `${zoom}|${topLeft}|${bottomRight}`;
    if (targetEncoded === lastAppliedRef.current) {
      return;
    }

    lastAppliedRef.current = targetEncoded;

    const leafletBounds = [
      [targetBounds.bounds.topLeft.lat, targetBounds.bounds.topLeft.lng],
      [
        targetBounds.bounds.bottomRight.lat,
        targetBounds.bounds.bottomRight.lng,
      ],
    ];

    map.fitBounds(leafletBounds);

    if (onApplied) {
      onApplied();
    }
  }, [map, targetBounds, onApplied]);

  return null;
}

// Outline of the searched location's boundary (the API's geoShape), drawn
// non-interactive so it never intercepts pin or map clicks.
function LocationShapeOutline({ shape }) {
  const map = useMap();

  useEffect(() => {
    if (!shape) return undefined;

    const layer = L.polygon(shape, {
      ...LOCATION_OUTLINE_STYLE,
      interactive: false,
    });
    layer.addTo(map);

    return () => {
      map.removeLayer(layer);
    };
  }, [map, shape]);

  return null;
}

// Fits the map to a location search's results extent. Unlike
// MapBoundsController the zoom is an output (fitBounds decides it), and the
// synchronous (animate: false) fit lets the caller read the final viewport
// back immediately and push it into the viewport filters. A null bounds (no
// geometry in the response) skips the fit but still reports the current
// viewport so the filters get re-established.
function LocationFitController({ target, onBeforeFit, onApplied }) {
  const map = useMap();

  useEffect(() => {
    if (!target) return;

    onBeforeFit();

    if (target.bounds) {
      map.fitBounds(target.bounds, {
        padding: [50, 50],
        maxZoom: 16,
        animate: false,
      });
    }

    const bounds = map.getBounds();
    onApplied({
      zoom: map.getZoom(),
      bounds: {
        topLeft: { lat: bounds.getNorth(), lng: bounds.getWest() },
        bottomRight: { lat: bounds.getSouth(), lng: bounds.getEast() },
      },
    });
    // eslint-disable-next-line
  }, [map, target]);

  return null;
}

// The aerial proxy is bbox-based rather than XYZ, so compute each tile's
// EPSG:3857 bbox (as a WMS layer would) and request it from the proxy.
function AerialTileLayer({ layerName }) {
  const map = useMap();

  useEffect(() => {
    const ProxyTileLayer = L.TileLayer.extend({
      getTileUrl(coords) {
        const [nw, se] = this._tileCoordsToNwSe(coords);
        const { crs } = map.options;
        return aerialTileUrl(
          layerName,
          L.bounds(crs.project(nw), crs.project(se)),
        );
      },
    });

    const tileLayer = new ProxyTileLayer('', {
      maxNativeZoom: MAP_MAX_NATIVE_ZOOM,
      maxZoom: MAP_MAX_ZOOM,
    });
    tileLayer.addTo(map);
    return () => {
      map.removeLayer(tileLayer);
    };
  }, [map, layerName]);

  return null;
}

// Showing/hiding the sidebar resizes the map container without a window resize,
// so Leaflet keeps its old size and the revealed strip shows blank tiles until
// invalidateSize runs.
function ResizeOnSidebarToggle({ sidebarHidden }) {
  const map = useMap();
  const isFirst = useRef(true);

  // Runs before paint so the tiles cover the revealed strip in the same frame.
  useLayoutEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    map.invalidateSize({ pan: false });
  }, [map, sidebarHidden]);

  return null;
}

export default function MapView({
  results,
  facets,
  setFilter,
  addFilter,
  removeFilter,
  filters,
  requestId,
  isLoading,
  searchType,
}) {
  const defaultCenter = [52.5, -1.5];
  const defaultZoom = 7;
  const ukBounds = [
    [49.5, -8],
    [61, 2],
  ];

  const { setMapState, mapState, mapStateRef } = useMapState();

  // On remount (e.g. returning from list view) the URL no longer carries the
  // viewport, so restore it from the persisted map state instead. A pending
  // location fit takes precedence over any restored viewport.
  const initialViewport = useMemo(() => {
    if (mapStateRef.current?.pendingLocationFit) return null;
    if (getMapBoundsFromURL()) return null;
    if (mapState?.bounds?.topLeft && mapState?.bounds?.bottomRight) {
      return { zoom: mapState.zoom || defaultZoom, bounds: mapState.bounds };
    }
    return null;
    // eslint-disable-next-line
  }, []);

  const [pendingViewport, setPendingViewport] = useState(null);
  const debouncedViewport = useDebounce(pendingViewport, 400);
  const lastTriggeredViewportRef = useRef(null);
  const isLayerUpdateRef = useRef(false);
  const setMapStateRef = useRef(setMapState);
  setMapStateRef.current = setMapState;

  const filterSourceRef = useRef(null);
  const currentFilterValueRef = useRef(null);
  // Seed true when restoring a viewport so the map's initial default-viewport
  // report doesn't overwrite the restore before it applies.
  const isApplyingExternalBoundsRef = useRef(Boolean(initialViewport));

  const [externalBoundsToApply, setExternalBoundsToApply] =
    useState(initialViewport);

  // Set once the response for a submit-with-location has arrived; consumed by
  // LocationFitController.
  const [locationFitTarget, setLocationFitTarget] = useState(null);

  const [activeBasemap, setActiveBasemap] = useState('os-light');
  const [basemapOpen, setBasemapOpen] = useState(false);
  const [activeTool, setActiveTool] = useState(null);
  const [scaleMetric, setScaleMetric] = useState(true);
  const [scaleData, setScaleData] = useState({
    label: '',
    width: 0,
    mapWidth: 0,
  });
  const [copyrightHeight, setCopyrightHeight] = useState(0);
  const [isPrinting, setIsPrinting] = useState(false);
  // Set once the map has been redrawn for print; mounting the sheet opens the
  // browser's print dialog.
  const [printSheet, setPrintSheet] = useState(null);

  useEffect(() => {
    if (activeTool) setBasemapOpen(false);
  }, [activeTool]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [bufferRadius, setBufferRadius] = useState(50);
  // Seed from a persisted shape filter so returning to the map detects the
  // already-drawn shape (disables the draw tools, shows the remove button).
  const [drawnShape, setDrawnShape] = useState(() =>
    getShapeFiltersFromFilters(filters) ? { restored: true } : null,
  );
  const [drawProgress, setDrawProgress] = useState({
    pointCount: 0,
    measurement: null,
  });
  const drawControlImperativeRef = useRef({});
  const mapContainerRef = useRef(null);

  const handleSelectResult = useCallback(item => {
    setSelectedResult(item);
    const el = mapContainerRef.current;
    if (!el) return;
    const delta = el.getBoundingClientRect().bottom - window.innerHeight;
    if (delta > 0) {
      window.scrollTo({ top: window.scrollY + delta, behavior: 'smooth' });
    }
  }, []);

  const initialShapeFilters = useMemo(
    () => getShapeFiltersFromFilters(filters),
    // eslint-disable-next-line
    [],
  );

  const hasSavedBounds =
    mapState?.bounds?.topLeft && mapState?.bounds?.bottomRight;

  const initialBounds = useMemo(() => {
    const urlBounds = getMapBoundsFromURL();
    if (urlBounds) {
      return [
        [urlBounds.bounds.topLeft.lat, urlBounds.bounds.topLeft.lng],
        [urlBounds.bounds.bottomRight.lat, urlBounds.bounds.bottomRight.lng],
      ];
    }

    if (hasSavedBounds) {
      return [
        [mapState.bounds.topLeft.lat, mapState.bounds.topLeft.lng],
        [mapState.bounds.bottomRight.lat, mapState.bounds.bottomRight.lng],
      ];
    }

    return ukBounds;
  }, []);

  const initialZoom = mapState?.zoom || defaultZoom;

  const [hasInitialized, setHasInitialized] = useState(hasSavedBounds);

  const boundsOptions = useMemo(
    () => (hasSavedBounds ? undefined : { padding: [50, 50] }),
    [hasSavedBounds],
  );

  const initialBoundsRef = useRef(initialBounds);
  const initialZoomRef = useRef(initialZoom);
  const boundsOptionsRef = useRef(boundsOptions);

  useEffect(() => {
    const urlBounds = getMapBoundsFromURL();

    if (urlBounds) {
      setMapStateRef.current(prev => ({
        ...prev,
        isMapMode: true,
        zoom: urlBounds.zoom,
        bounds: urlBounds.bounds,
      }));
    } else {
      setMapStateRef.current(prev => ({ ...prev, isMapMode: true }));
    }

    return () => {
      setMapStateRef.current(prev => ({
        ...prev,
        isMapMode: false,
        visibleCount: null,
      }));
    };
  }, []);

  const hasViewportChanged = viewport => {
    return !viewportsMatch(lastTriggeredViewportRef.current, viewport);
  };

  const setFilterRef = useRef(setFilter);
  setFilterRef.current = setFilter;
  const addFilterRef = useRef(addFilter);
  addFilterRef.current = addFilter;
  const removeFilterRef = useRef(removeFilter);
  removeFilterRef.current = removeFilter;

  // Record a viewport as the map-driven source of truth and push it into the
  // viewport filters (triggering a search clipped to it).
  const applyViewportFilters = useCallback(
    viewport => {
      lastTriggeredViewportRef.current = normalizeViewport(viewport);
      // Merge so UI-only state (e.g. sidebarHidden) survives a pan/zoom.
      setMapState(prev => ({
        ...prev,
        isMapMode: true,
        zoom: viewport.zoom,
        bounds: viewport.bounds,
      }));

      const { zoom, topLeft, bottomRight } = encodeMapBounds(viewport);
      currentFilterValueRef.current = `${zoom}|${topLeft}|${bottomRight}`;
      filterSourceRef.current = 'map';

      setTimeout(() => {
        setFilterRef.current('mapZoom', zoom, 'all');
        setFilterRef.current('mapBoundsTopLeft', topLeft, 'all');
        setFilterRef.current('mapBoundsBottomRight', bottomRight, 'all');
      }, 0);
    },
    [setMapState],
  );

  useEffect(() => {
    if (pendingViewport && !hasInitialized) {
      if (isApplyingExternalBoundsRef.current) {
        return;
      }
      // A pending location fit completes initialization itself once the
      // response arrives; don't let the map's initial viewport report clip
      // that response to the old view.
      if (mapStateRef.current?.pendingLocationFit) {
        return;
      }

      setHasInitialized(true);
      applyViewportFilters(pendingViewport);
    }
  }, [pendingViewport, hasInitialized, applyViewportFilters, mapStateRef]);

  useEffect(() => {
    if (
      debouncedViewport &&
      hasInitialized &&
      hasViewportChanged(debouncedViewport)
    ) {
      if (isApplyingExternalBoundsRef.current) {
        return;
      }
      // Ignore viewport reports while a location fit is pending (the fit
      // supersedes them and re-establishes the filters when it applies).
      if (mapStateRef.current?.pendingLocationFit) {
        return;
      }

      applyViewportFilters(debouncedViewport);
    }
  }, [debouncedViewport, hasInitialized, applyViewportFilters, mapStateRef]);

  useEffect(() => {
    const filterBounds = getMapBoundsFromFilters(filters);

    if (!filterBounds) {
      return;
    }

    const zoomFilter = filters.find(f => f.field === 'mapZoom')?.values[0];
    const topLeftFilter = filters.find(f => f.field === 'mapBoundsTopLeft')
      ?.values[0];
    const bottomRightFilter = filters.find(
      f => f.field === 'mapBoundsBottomRight',
    )?.values[0];
    const currentEncodedValue = `${zoomFilter}|${topLeftFilter}|${bottomRightFilter}`;

    if (currentEncodedValue === currentFilterValueRef.current) {
      if (filterSourceRef.current === 'map') {
        filterSourceRef.current = null;
      }
      return;
    }

    currentFilterValueRef.current = currentEncodedValue;

    isApplyingExternalBoundsRef.current = true;

    setMapState(prev => ({
      ...prev,
      isMapMode: true,
      zoom: filterBounds.zoom,
      bounds: filterBounds.bounds,
    }));

    setExternalBoundsToApply(filterBounds);
  }, [filters]);

  // Defensive: if anything removes the map viewport filters (mapZoom/bounds)
  // while the map itself hasn't moved, reinstate them from the last viewport so
  // the map stays in sync with the search request. A submit-with-location
  // removes them ON PURPOSE (so the response isn't clipped to the old view) —
  // the pending fit re-establishes them once it applies.
  useEffect(() => {
    if (!hasInitialized || isApplyingExternalBoundsRef.current) return;
    if (mapStateRef.current?.pendingLocationFit) return;

    const hasMapBounds = filters.some(f => f.field === 'mapZoom');
    const lastViewport = lastTriggeredViewportRef.current;
    if (hasMapBounds || !lastViewport) return;

    const { zoom, topLeft, bottomRight } = encodeMapBounds(lastViewport);
    currentFilterValueRef.current = `${zoom}|${topLeft}|${bottomRight}`;
    filterSourceRef.current = 'map';

    setTimeout(() => {
      setFilterRef.current('mapZoom', zoom, 'all');
      setFilterRef.current('mapBoundsTopLeft', topLeft, 'all');
      setFilterRef.current('mapBoundsBottomRight', bottomRight, 'all');
    }, 0);
  }, [filters, hasInitialized, mapStateRef]);

  const geotileData = facets?.mapTileCluster?.[0]?.data;
  const hasGeotileClusters = geotileData && geotileData.length > 0;
  const hasClusters = hasGeotileClusters;

  const geotileMarkers = useMemo(
    () =>
      hasGeotileClusters
        ? geotileData
            .map(item => {
              const tile = decodeGeotile(item.value);
              if (!tile) return null;
              return {
                id: item.value,
                bounds: tile.bounds,
                center: tile.center,
                count: item.count,
              };
            })
            .filter(Boolean)
        : [],
    [geotileData, hasGeotileClusters],
  );

  const individualMarkers = useMemo(
    () =>
      !hasClusters
        ? results
            .map((result, index) => {
              const geoPointValue = result.geoPoint?.raw || result.geoPoint;
              const position = parseGeoPoint(geoPointValue);
              const geoShapeValue = result.geoShape?.raw || result.geoShape;
              const shape = parseGeoShape(geoShapeValue);
              if (!position && !shape) return null;
              return {
                id: result.id?.raw || result.id || index,
                position,
                shape,
                title: result.name?.raw || result.name || 'Heritage Site',
                result,
              };
            })
            .filter(Boolean)
        : [],
    [results, hasClusters],
  );

  // GeoJSON boundary of the searched location, as Leaflet latlngs (null when
  // no location search is applied).
  const locationShape = useMemo(
    () => parseGeoShape(facets?.locationGeoShape),
    [facets],
  );

  // If the fit was requested while the map was unmounted (submit from list
  // view), the results in state at mount belong to the previous (list-mode)
  // request and carry no geometry — re-arm the flag so it waits for the first
  // response after mounting in map mode.
  useEffect(() => {
    const pending = mapStateRef.current?.pendingLocationFit;
    if (pending && pending.requestIdAtSubmit !== requestId) {
      setMapState(prev => ({
        ...prev,
        pendingLocationFit: { ...pending, requestIdAtSubmit: requestId },
      }));
    }
    // eslint-disable-next-line
  }, []);

  // Consume a pending location fit (set by Header on submit-with-location)
  // once the response for that submit has arrived, and fit the map to the
  // extent of its results.
  useEffect(() => {
    const pending = mapStateRef.current?.pendingLocationFit;
    if (!pending || isLoading) return;
    if (requestId === pending.requestIdAtSubmit) return;

    setMapState(prev => ({ ...prev, pendingLocationFit: null }));

    // The location was cleared while the request was in flight — nothing to
    // fit; normal viewport behaviour resumes.
    if (!filters.some(f => f.field === LOCATION_FILTER_FIELD)) {
      return;
    }

    // Fit to the searched location's own geometry (the API returns it as a
    // top-level geoShape), falling back to the results' extent.
    const bounds =
      computeLatLngsExtent(locationShape) ||
      computeMarkersExtent(individualMarkers, geotileMarkers);

    setLocationFitTarget({ bounds });
  }, [
    requestId,
    isLoading,
    individualMarkers,
    geotileMarkers,
    locationShape,
    filters,
    setMapState,
    mapStateRef,
  ]);

  const handleLocationFitApplied = useCallback(
    viewport => {
      setLocationFitTarget(null);
      setHasInitialized(true);
      applyViewportFilters(viewport);
      setTimeout(() => {
        isApplyingExternalBoundsRef.current = false;
      }, 500);
    },
    [applyViewportFilters],
  );

  const totalMapRecords = facets?.totalMapRecords;
  useEffect(() => {
    setMapStateRef.current(prev => ({ ...prev, totalMapRecords }));
  }, [totalMapRecords]);

  // Close the result dialog if its marker leaves the result set (search
  // filtered, or panned/zoomed away, or clusters replacing individual markers).
  useEffect(() => {
    if (!selectedResult) return;
    const stillVisible = individualMarkers.some(
      m => m.id === selectedResult.id,
    );
    if (!stillVisible) setSelectedResult(null);
  }, [individualMarkers, selectedResult]);

  const handleViewportChange = newViewport => {
    setPendingViewport(newViewport);
  };

  const handleClear = useCallback(() => {
    setDrawnShape(null);
    setActiveTool(null);
    drawControlImperativeRef.current.clearAll?.();
    removeFilterRef.current('mapShapeType');
    removeFilterRef.current('mapShapeRadius');
    removeFilterRef.current('mapShapePoints');
  }, []);

  const handleShapeDrawn = useCallback(shape => {
    setDrawnShape(shape);
    setActiveTool(null);
    if (shape) {
      const encoded = encodeShapeFilters(shape);

      if (encoded) {
        setFilterRef.current('mapShapeType', encoded.shapeType, 'all');

        if (encoded.shapeRadius) {
          setFilterRef.current('mapShapeRadius', encoded.shapeRadius, 'all');
        } else {
          removeFilterRef.current('mapShapeRadius');
        }

        removeFilterRef.current('mapShapePoints');

        encoded.shapePoints.forEach(point => {
          addFilterRef.current('mapShapePoints', point, 'all');
        });
      }
    } else {
      removeFilterRef.current('mapShapeType');
      removeFilterRef.current('mapShapeRadius');
      removeFilterRef.current('mapShapePoints');
    }
  }, []);

  // Mirror an external shape-filter removal (the "Selected area" chip) by
  // clearing the drawn layer on the map.
  useEffect(() => {
    const hasShapeFilter = filters.some(f => f.field === 'mapShapeType');
    if (!hasShapeFilter && drawnShape) {
      setDrawnShape(null);
      setActiveTool(null);
      drawControlImperativeRef.current.clearAll?.();
    }
  }, [filters, drawnShape]);

  const activeBasemapDef = BASEMAPS.find(b => b.id === activeBasemap);

  // Redraws the current view offscreen, then hands the resulting sheet to the
  // browser's print dialog (which is also where the user can save it as a PDF).
  // The Leaflet instance comes from the control, inside MapContainer.
  const handlePrint = async printMap => {
    setIsPrinting(true);
    try {
      const image = await renderMapImage({
        map: printMap,
        basemap: activeBasemapDef,
        markers: individualMarkers,
        geotileMarkers: hasClusters ? geotileMarkers : [],
        selectedId: selectedResult?.id,
        locationShape,
        drawnShapes: drawControlImperativeRef.current.getShapes?.() || [],
      });
      setPrintSheet({
        image,
        scale: scaleData,
        copyrightText: activeBasemapDef?.description,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Could not prepare the map for printing', error);
      setIsPrinting(false);
    }
  };

  return (
    <>
      <div className={mapContainer} ref={mapContainerRef}>
        <MapContainer
          attributionControl={false}
          bounds={initialBoundsRef.current}
          boundsOptions={boundsOptionsRef.current}
          center={defaultCenter}
          keyboard={false}
          maxZoom={MAP_MAX_ZOOM}
          minZoom={defaultZoom}
          zoom={initialZoomRef.current}
          zoomControl={false}
        >
          {activeBasemapDef?.type === 'os' && (
            <TileLayer
              attribution={`&copy; ${activeBasemapDef.description}`}
              maxNativeZoom={MAP_MAX_NATIVE_ZOOM}
              maxZoom={MAP_MAX_ZOOM}
              url={OS_TILE_URL.replace('{layer}', activeBasemapDef.layer)}
            />
          )}
          {activeBasemapDef?.type === 'aerial' && (
            <AerialTileLayer layerName={activeBasemapDef.layer} />
          )}

          <MapEventHandler
            isLayerUpdateRef={isLayerUpdateRef}
            lastTriggeredViewportRef={lastTriggeredViewportRef}
            onViewportChange={handleViewportChange}
          />

          <ResizeOnSidebarToggle sidebarHidden={mapState?.sidebarHidden} />

          <ScaleComputer metric={scaleMetric} onChange={setScaleData} />

          <LeftControlStack
            defaultCenter={defaultCenter}
            defaultZoom={defaultZoom}
            isPrinting={isPrinting}
            onPrint={handlePrint}
          />
          <RightControlStack
            activeBasemap={activeBasemap}
            activeTool={activeTool}
            basemapOpen={basemapOpen}
            hasDrawnShape={Boolean(drawnShape)}
            onBasemapOpenChange={setBasemapOpen}
            onBasemapSelect={setActiveBasemap}
            onClear={handleClear}
            onToolSelect={setActiveTool}
          />

          {externalBoundsToApply && (
            <MapBoundsController
              onApplied={() => {
                setExternalBoundsToApply(null);
                setTimeout(() => {
                  isApplyingExternalBoundsRef.current = false;
                }, 500);
              }}
              targetBounds={externalBoundsToApply}
            />
          )}

          {locationFitTarget && (
            <LocationFitController
              onApplied={handleLocationFitApplied}
              onBeforeFit={() => {
                isApplyingExternalBoundsRef.current = true;
              }}
              target={locationFitTarget}
            />
          )}

          <LocationShapeOutline shape={locationShape} />

          {hasClusters && (
            <GeotileMarkers
              isLayerUpdateRef={isLayerUpdateRef}
              showGrid={true}
              tiles={geotileMarkers}
            />
          )}
          {!hasClusters && (
            <IndividualMarkers
              markers={individualMarkers}
              onSelect={handleSelectResult}
              selectedId={selectedResult?.id}
            />
          )}

          <DrawControl
            activeTool={activeTool}
            bufferRadius={bufferRadius}
            drawControlRef={drawControlImperativeRef}
            initialShape={initialShapeFilters}
            isLayerUpdateRef={isLayerUpdateRef}
            onDrawProgress={setDrawProgress}
            onShapeDrawn={handleShapeDrawn}
            onToolEnd={() => setActiveTool(null)}
          />

          <DrawCursorTooltip
            activeTool={activeTool}
            measurement={drawProgress.measurement}
            pointCount={drawProgress.pointCount}
          />
        </MapContainer>

        <MapScale
          label={scaleData.label}
          metric={scaleMetric}
          onToggle={() => setScaleMetric(prev => !prev)}
          width={scaleData.width}
        />
        <MapCopyright
          onHeightChange={setCopyrightHeight}
          text={activeBasemapDef?.description}
        />

        {activeTool && (
          <ToolPanel
            bufferRadius={bufferRadius}
            canDeleteLastPoint={drawProgress.pointCount > 0}
            canFinish={
              activeTool === 'polygon'
                ? drawProgress.pointCount >= 3
                : drawProgress.pointCount >= 2
            }
            onBufferChange={setBufferRadius}
            onClose={() => drawControlImperativeRef.current.cancel?.()}
            onDeleteLastPoint={() =>
              drawControlImperativeRef.current.deleteLastPoint?.()
            }
            onFinish={() => drawControlImperativeRef.current.finish?.()}
            pointerOffset={20}
            tool={activeTool}
            topOffset={60 + TOOL_BUTTON_INDEX[activeTool] * 40}
          />
        )}

        <SidebarCollapseTab
          isOpen={!mapState?.sidebarHidden}
          onToggle={() =>
            setMapStateRef.current(prev => ({
              ...prev,
              sidebarHidden: !prev.sidebarHidden,
            }))
          }
        />

        {selectedResult && (
          <ResultPopup
            bottomOffset={copyrightHeight + 10}
            onClose={() => setSelectedResult(null)}
            result={buildPopupResult(selectedResult, searchType)}
            variant={selectedResult.shape ? 'shape' : 'point'}
          />
        )}
      </div>

      {printSheet && (
        <MapPrintSheet
          copyrightText={printSheet.copyrightText}
          image={printSheet.image}
          onDone={() => {
            setPrintSheet(null);
            setIsPrinting(false);
          }}
          scale={printSheet.scale}
        />
      )}
      <p className={mapNotice}>
        Use of this mapping and aerial photography data is subject to{' '}
        <a href="https://historicengland.org.uk/terms/website-terms-conditions/">
          terms and conditions
        </a>
      </p>
    </>
  );
}
